// Export a learning module to a PDF — matching the admin portal's output — from its markdown
// content: title/description, video and attachment notes, then a lightweight subset of the block
// markdown (headings, bold/italic/code, links, bullet/numbered lists) and embedded images.
//
// Image embedding is best-effort: pdfmake needs the raw bytes as a PNG/JPEG data URL, so the
// image is fetched via its presigned URL and read into a data URL. That requires the bucket to
// allow cross-origin reads (CORS); if a fetch fails, or the type is unsupported (svg/gif/webp),
// the image is skipped and its caption/alt is noted instead so the export still succeeds.
//
// The markdown → pdfmake block parser lives in ./markdownToPdfContent (kept pure for testing).
//
// DUPLICATED: ported from admin-portal-v5 src/features/learningModule/utils/moduleToPdf.ts, which
// is the source of truth. The two must produce identical PDFs, so keep them in sync — the only
// intended dealer-side deltas are the two below (CDN fonts + ./assets import).
// TODO(monorepo): when we move to the monorepo starter, extract this + markdownToPdfContent into a
// shared package (e.g. @hopdrive/learning-center) consumed by both portals, and delete both copies.

import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import pdfMake from 'pdfmake/build/pdfmake';
import type { LearningModule } from '../types';
import { resolveAssetUrl, isPrivateAssetUrl } from './assets';
import { markdownToContent } from './markdownToPdfContent';

// Importing the bundled vfs_fonts breaks the build, so load Roboto from a CDN — the same
// workaround the invoice PDF service uses (src/services/pdfService.ts). pdfMake is a singleton, so
// setting this here is idempotent with that module.
pdfMake.fonts = {
  Roboto: {
    normal: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
    bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
    italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
    bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf',
  },
};

// ── images ──────────────────────────────────────────────────────────────────────

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Fetch an image and return a PNG/JPEG data URL, or null if it can't be embedded. */
async function fetchImageDataUrl(url: string): Promise<string | null> {
  try {
    const resolved = await resolveAssetUrl(url);
    const res = await fetch(resolved);
    if (!res.ok) return null;
    const blob = await res.blob();
    // pdfmake's `image` only supports JPEG/PNG.
    if (!/^image\/(png|jpe?g)$/i.test(blob.type)) return null;
    return await blobToDataUrl(blob);
  } catch {
    return null;
  }
}

// ── body markdown → content ─────────────────────────────────────────────────────

/**
 * Convert the module's markdown body to pdfmake content. Text (headings, emphasis, code,
 * links, lists) goes through markdownToContent. A standalone image line `![alt](url)` is
 * embedded best-effort via its presigned URL (JPEG/PNG only, like the reader). If it can't be
 * embedded, a public URL falls back to a link note, but a private-bucket URL (which would 403 if
 * followed) is noted without the dead link. An inline image (one not alone on its line) can't be
 * embedded, so parseInline degrades it to its alt text rather than a dead clickable link.
 */
const IMAGE_LINE = /^!\[([^\]]*)\]\(([^)]+)\)\s*$/;

async function bodyToContent(md: string): Promise<Content[]> {
  const out: Content[] = [];
  let buf: string[] = [];
  const flushText = () => {
    if (buf.length) {
      out.push(...markdownToContent(buf.join('\n')));
      buf = [];
    }
  };
  // Track fence state (same open/close rule as markdownToContent) so image markdown inside a
  // fenced code sample stays verbatim code — extracting it would embed the "image" AND split
  // the fence across chunks, flipping code/prose rendering for the rest of the body.
  let fence: { char: string; len: number } | null = null;
  for (const line of (md || '').split('\n')) {
    const f = /^\s{0,3}(`{3,}|~{3,})/.exec(line.trimEnd());
    if (f) {
      if (!fence) fence = { char: f[1][0], len: f[1].length };
      else if (f[1][0] === fence.char && f[1].length >= fence.len) fence = null;
      buf.push(line);
      continue;
    }
    const img = fence ? null : IMAGE_LINE.exec(line.trim());
    if (!img) {
      buf.push(line);
      continue;
    }
    flushText();
    const [, alt, url] = img;
    const dataUrl = await fetchImageDataUrl(url);
    if (dataUrl) {
      out.push({ image: dataUrl, fit: [500, 360], margin: [0, 4, 0, alt ? 1 : 8] });
      if (alt) out.push({ text: alt, style: 'caption' });
    } else if (isPrivateAssetUrl(url)) {
      // Uploaded asset on a private bucket that we couldn't embed (CORS/unsupported type). The raw
      // URL 403s if followed, so note the omission WITHOUT the dead link — just the alt, if any.
      out.push({ text: alt ? `[Image: ${alt}]` : '[Image could not be included]', style: 'caption', margin: [0, 2, 0, 6] });
    } else {
      // External/public URL — safe to surface as a link the reader can actually open.
      out.push({ text: `[${alt || 'Image'} — see ${url}]`, style: 'link', margin: [0, 2, 0, 6] });
    }
  }
  flushText();
  return out;
}

async function buildDocDefinition(module: LearningModule): Promise<TDocumentDefinitions> {
  const content: Content[] = [{ text: module.name, style: 'title' }];
  if (module.description) content.push({ text: module.description, style: 'description' });
  // The hero video is often the module's primary content — surface it as a link (public
  // YouTube URLs are safe to follow, unlike private-bucket assets).
  if (module.hero_video_url) {
    content.push({
      text: [{ text: 'Video: ', bold: true }, { text: module.hero_video_url, link: module.hero_video_url, style: 'link' }],
      margin: [0, 0, 0, 8],
    });
  }
  // The attachment lives on the private documents bucket in the usual case, where a raw link
  // would 403 — note its existence without a dead link (same rule as image fallbacks).
  if (module.attachment_url) {
    content.push(
      isPrivateAssetUrl(module.attachment_url)
        ? {
            text: '[This training has an attached document — download it from the training page]',
            style: 'caption',
            margin: [0, 0, 0, 8],
          }
        : {
            text: [{ text: 'Attachment: ', bold: true }, { text: module.attachment_url, link: module.attachment_url, style: 'link' }],
            margin: [0, 0, 0, 8],
          },
    );
  }
  content.push(...(await bodyToContent(module.content ?? '')));

  return {
    content,
    info: { title: module.name },
    pageMargins: [48, 48, 48, 56],
    defaultStyle: { font: 'Roboto', fontSize: 11, lineHeight: 1.3 },
    styles: {
      title: { fontSize: 20, bold: true, margin: [0, 0, 0, 4] },
      description: { fontSize: 11, color: '#606670', margin: [0, 0, 0, 12] },
      sectionTitle: { fontSize: 15, bold: true, margin: [0, 14, 0, 6] },
      h1: { fontSize: 16, bold: true, margin: [0, 6, 0, 4] },
      h2: { fontSize: 14, bold: true, margin: [0, 6, 0, 4] },
      h3: { fontSize: 12, bold: true, margin: [0, 4, 0, 2] },
      caption: { fontSize: 9, italics: true, color: '#788088', margin: [0, 0, 0, 8] },
      link: { color: '#4068a0', decoration: 'underline' },
    },
    footer: (currentPage: number, pageCount: number) => ({
      text: `${currentPage} / ${pageCount}`,
      alignment: 'center',
      fontSize: 9,
      color: '#abb0b5',
      margin: [0, 8, 0, 0],
    }),
  };
}

/** Build and download the module as a PDF. */
export async function downloadModulePdf(module: LearningModule): Promise<void> {
  const dd = await buildDocDefinition(module);
  const name = (module.key || module.name || 'module').replace(/[^\w.-]+/g, '-').toLowerCase();
  pdfMake.createPdf(dd).download(`${name}.pdf`);
}
